/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file misc files - based on sentry
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

import {isUndefined, isPlainObject, isString, isFunction, isNumber, isArray, isBoolean, isRegExp} from './is';
import {supportsURL} from './supports';
import {stringify} from '../vender/stringify';
import {StackFrame, Payload, Behavior, APIException} from '../interface/options.interface';
import {md5} from '../vender/md5';

/** 最大序列化长度 */
const MAX_SERIALIZE_KEYS_LENGTH = 40;

/** 序列化最大深度 */
const MAX_SERIALIZE_EXCEPTION_DEPTH = 3;

/** 异常序列化最大负载 */
const MAX_SERIALIZE_EXCEPTION_SIZE = 50 * 1024;

/**
 * 检测某个对象是否拥有某个属性
 * @param obj 待检测对象
 * @param key 待检测属性，支持 string | symbol | number 类型
 * @returns boolean
 */
export function hasKey(obj: any, key: any) {
    return Reflect.has(obj, key);
}

/**
 * 遍历对象或者数组，调用回调函数处理
 * @param obj 对象或者数组
 * @param callback 回调函数，接收两个参数，如果是对象第一个参数为 key，第二个参数为 value，如果是数组，第一个参数位索引，第二个参数为数组值
 */
export function each(obj: object | Array<any>, callback: (k: any, v: any) => any) {
    if (Array.isArray(obj)) {
        // 数组
        const len = obj.length;
        for (let i = 0; i < len; i++) {
            callback.call(null, i, obj[i]);
        }
    } else {
        for (const k in obj) {
            if (hasKey(obj, k)) {
                callback.call(null, k, obj[k]);
            }
        }
    }
}

/**
 * 将对象转化为URL查询字符串
 * @param params 参数对象
 * @returns string
 */
export function urlencode(params: {[k: string]: any}) {
    // 如果支持 URL 直接使用较新的 API
    if (supportsURL()) {
        return new URLSearchParams(params).toString();
    }

    // 否则使用循环
    const pairs: string[] = [];
    each(params, function (key: string, value: string) {
        pairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    });
    return pairs.join('&');
}

/**
 * 如果对象值是函数，返回函数调用计算完毕后的值对象
 * @param hash 参数对象
 * @returns object
 */
export function evaluateHash(hash: {[k: string]: any}): object {
    const evaluated = {};

    for (const key in hash) {
        if (hash.hasOwnProperty(key)) {
            const value = hash[key];
            evaluated[key] = isFunction(value) ? value() : value;
        }
    }

    return evaluated;
}

/**
 * 序列化消息 Keys，最长 maxLength 个字符，超出末尾添加 ...
 * @param keys 键列表
 * @param maxLength 最大长度
 * @returns string
 */
export function serializeKeysForMessage(keys: any, maxLength = MAX_SERIALIZE_KEYS_LENGTH): string {
    if (isNumber(keys) || isString(keys)) {
        return keys.toString();
    }
    if (!isArray(keys)) {
        return '';
    }

    keys = keys.filter((key: any) => isString(key));
    if (keys.length === 0) {
        return '[object has no keys]';
    }

    maxLength = isNumber(maxLength) ? maxLength : MAX_SERIALIZE_KEYS_LENGTH;

    // 如果第一个字符串已经超出长度，直接返回第一个字符串
    if (keys[0].length >= maxLength) {
        return keys[0];
    }

    // 首先取所有键，如果拼接长度没有超出，返回，否则逐步减少键个数
    for (let usedKeys = keys.length; usedKeys > 0; usedKeys--) {
        const serialized = keys.slice(0, usedKeys).join(', ');

        // 过长了，减去最后一个值，重新遍历
        if (serialized.length > maxLength) {
            continue;
        }

        // 第一次循环就满足，直接返回即可
        if (usedKeys === keys.length) {
            return serialized;
        }

        // 否则加上 ... 符号
        return serialized + '\u2026';
    }

    return '';
}

/**
 * 递归序列化对象/数组，避免 JSON 直接序列化会丢失函数、正则等问题
 * @param value 对象，可能为数组
 * @param depth 序列化的深度，如果为0，对象直接序列化为 '[Object]'，数组直接序列化为 '[Array]' 等
 * @returns string | object，如果为0返回字符串，否则返回对象
 */
export function serializeObject(value: any, depth: number): any {
    if (depth === 0) {
        return serializeValue(value);
    }

    if (isPlainObject(value)) {
        return Object.keys(value).reduce(function (acc, key) {
            acc[key] = serializeObject(value[key], depth - 1);
            return acc;
        }, {});
    } else if (Array.isArray(value)) {
        return value.map(function (val) {
            return serializeObject(val, depth - 1);
        });
    }

    return serializeValue(value);
}

/**
 * 序列化异常信息
 * @param ex 异常对象
 * @param depth 对象层级深度
 * @param maxSize 最大长度
 * @returns string | object，非对象直接返回字符串，否则返回处理后的对象
 */
export function serializeException(
    ex: any,
    depth = MAX_SERIALIZE_EXCEPTION_DEPTH,
    maxSize = MAX_SERIALIZE_EXCEPTION_SIZE
): string {
    // 非对象，直接返回
    if (!isPlainObject(ex)) {
        return ex + '';
    }

    const serialized = serializeObject(ex, depth);

    // 如果异常信息超长，递归减少一层
    if (jsonSize(stringify(serialized)) > maxSize) {
        return serializeException(ex, depth - 1);
    }

    return serialized;
}

/**
 * 序列化值为字符串
 * @param value 待序列化的值
 * @returns string
 */
export function serializeValue(value: any): string {
    if (isString(value)) {
        const maxLength = 40;
        return truncate(value, maxLength);
    } else if (isNumber(value) || isBoolean(value) || isUndefined(value)) {
        return value + '';
    }

    // Node.js REPL notation
    if (isPlainObject(value)) {
        return '[Object]';
    }
    if (isArray(value)) {
        return '[Array]';
    }
    if (isFunction(value)) {
        return value.name ? '[Function: ' + value.name + ']' : '[Function]';
    }

    return value;
}

/**
 * 获取对象 JSON.stringify 后的字节长度，会带上两个双引号的长度
 * 例如 jsonSize('abc') 会返回 5，因为序列化后的字符串为 "abc"，jsonSize('你好!') 返回 9
 * @param value 对象
 * @returns JSON.stringify 后的长度
 */
export function jsonSize(value: any): number {
    return utf8Length(JSON.stringify(value));
}

/**
 * 获取字符串utf8字节长度
 * @param str 待获取长度的字符串
 * @returns 字符串utf8字节长度
 */
export function utf8Length(str: string): number {
    return ~-encodeURI(str).split(/%..|./).length;
}

/**
 * 字符串截断，超出长度添加 '...'，因此如果截断后，长度为 max + 1
 * @param str 待截断字符串
 * @param max 待截断的位数
 * @returns string
 */
export function truncate(str: string, max: number): string {
    if (max === 0) {
        return str;
    }
    return str.length <= max ? str : str.substr(0, max) + '\u2026';
}

// borrowed from https://tools.ietf.org/html/rfc3986#appendix-B
// intentionally using regex and not <a/> href parsing trick because React Native and other
// environments where DOM might not be available
// TODO: 为什么不使用 parse-url 库
export function parseUrl(url: string) {
    const defaultObj = {
        protocol: '',
        host: '',
        path: '',
        relative: ''
    };

    if (typeof url !== 'string') {
        return defaultObj;
    }
    const match = url.match(/^(([^:\/?#]+):)?(\/\/([^\/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?$/);

    // coerce to undefined values to empty string so we don't get 'undefined'
    if (match && match.length) {
        const query = match[6] || '';
        const fragment = match[8] || '';
        return {
            protocol: match[2] || '',
            host: match[4] || '',
            path: match[5] || '',
            relative: (match[5] || '') + query + fragment // everything minus origin
        };
    }

    return defaultObj;
}

/**
 * Given a child DOM element, returns a query-selector statement describing that
 * and its ancestors
 * e.g. [HTMLElement] => body > div > input#foo.btn[name=baz]
 * @param elem
 * @returns 元素路径字符串，递归上级节点，进行拼接
 */
export function htmlTreeAsString(elem: Node) {
    /* eslint no-extra-parens:0*/
    const MAX_TRAVERSE_HEIGHT = 5;
    const MAX_OUTPUT_LEN = 80;
    const out: string[] = [];
    const separator = ' > ';
    const sepLength = separator.length;
    let nextStr: string;
    let height = 0;
    let len = 0;

    while (elem && height++ < MAX_TRAVERSE_HEIGHT) {
        nextStr = htmlElementAsString(elem as HTMLElement);
        // 如果到达 html 节点，或者到达长度限制，退出
        const isHTMLNode = nextStr === 'html';
        const reachMaxOutputLen = height > 1 && len + out.length * sepLength + nextStr.length >= MAX_OUTPUT_LEN;
        if (isHTMLNode || reachMaxOutputLen) {
            break;
        }

        out.push(nextStr);
        len += nextStr.length;
        elem = elem.parentNode;
    }

    return out.reverse().join(separator);
}

/**
 * Returns a simple, query-selector representation of a DOM element
 * e.g. [HTMLElement] => input#foo.btn[name=baz]
 * @param el HTMLElement
 * @returns 元素路径字符串
 */
export function htmlElementAsString(el: HTMLElement): string {
    if (!el || !el.tagName) {
        return '';
    }

    const out = [el.tagName.toLowerCase()];
    if (el.id) {
        out.push('#' + el.id);
    }

    const classes = el.className ? el.className.split(/\s+/) : [];
    for (const cls of classes) {
        out.push('.' + cls);
    }

    const attrWhitelist = ['type', 'name', 'title', 'alt'];
    for (const k of attrWhitelist) {
        const v = el.getAttribute(k);
        if (v) {
            out.push('[' + k + '=' + v + ']');
        }
    }

    return out.join('');
}

/**
 * 检测两个堆栈是否相同，算法如下：
 * 1. 长度比较
 * 2. 每一帧比较
 */
export function isSameStacktrace(stack1: StackFrame[], stack2: StackFrame[]): boolean {
    if (hasArrayValue(stack1) && hasArrayValue(stack2)) {
        const l1 = stack1.length;
        const l2 = stack2.length;

        if (l1 !== l2) {
            return false;
        }

        for (let i = 0; i < l1; i++) {
            const s1 = stack1[i];
            const s2 = stack2[i];

            if (!compareFrame(s1, s2)) {
                return false;
            }
        }
        return true;
    }
    return false;
}

/**
 * 检测两个异常是否相同，算法如下：
 * 1. 检测 mechanism、type、value、url
 * 2. 检测两个堆栈是否相同
 */
export function isSameException(e1: any, e2: any): boolean {
    // 如果值不同，直接返回 false
    if (e1.mechanism !== e2.mechanism || e1.type !== e2.type || e1.value !== e2.value || e1.url !== e2.url) {
        return false;
    }

    const allStackEmpty = e1.stacktrace.length === 0 && e2.stacktrace.length === 0;

    // 如果堆栈全部为空 []，那么属于同一个异常
    return allStackEmpty ? true : isSameStacktrace(e1.stacktrace, e2.stacktrace);
}

/**
 * 获取异常的字符串表现形式，后续根据该字符串计算 md5，获取 event id
 * @param 负载参数
 * @returns 异常的字符串表现形式
 */
export function getExceptionString(payload: Payload): string {
    const ex: any = payload.exception;
    const exDigest: any[] = [];

    exDigest.push('token=' + ex.token); // 项目
    exDigest.push('type=' + ex.type); // 类型
    exDigest.push('mechanism=' + ex.mechanism); // 异常捕获方式
    exDigest.push('value=' + ex.value); // 消息
    exDigest.push('url=' + ex.url); // 来源 URL
    if (ex.stacktrace) {
        ex.stacktrace.forEach((s: any) => {
            exDigest.push('filename=' + s.filename || '');
            exDigest.push('lineno=' + s.lineno || '');

            // 这里在不同的浏览器中也会有微小差异，例如 148366 和 148377
            // 奇怪的是两者计算出来的原始行列是一样的。
            exDigest.push('colno=' + s.colno || '');

            // 有时候会不一致，例如：
            // Object.C.e.Qb.t[as updateDirectives] 和 Object.updateDirectives
            // e.a[as_next] 和 e.object.a[as _next]
            // 因此考虑对比异常时，将函数排除名称
            // exDigest.push('function=' + s['function'] || '');
        });
    }

    return exDigest.join('***');
}

/**
 * 获取异常的字符串表现形式，后续根据该字符串计算 md5，获取 data id
 * 与 getExceptionString 不同的是，这里会计算环境信息
 * @param 负载参数
 * @returns 异常的字符串表现形式
 */
export function getAllExceptionString(payload: Payload): string {
    // 基本信息
    const basicInfo = getExceptionString(payload);

    const exDigest: string[] = [];

    // 环境信息
    const {request, env, behaviors} = payload;

    // 某些情况下，暂时无法复现，request 会为 undefined
    // 但是在 _trimPacket 函数中会保证这个函数存在，所以比较奇怪，这里做下兼容处理
    if (!request) {
        return JSON.stringify(payload);
    }

    const {headers, url} = request;

    exDigest.push('Referrer=' + headers.Referrer);
    exDigest.push('ua=' + headers['User-Agent']);
    exDigest.push('url=' + url);

    each(env, (k, v) => {
        if (k !== 'session:duration') {
            exDigest.push(`${k}=${v}`);
        }
    });

    each(behaviors, (behavior: Behavior) => {
        exDigest.push(`behavior.category=${behavior.category}`);
        exDigest.push(`behavior.name=${behavior.name}`);
        exDigest.push(`behavior.message=${behavior.message}`);
    });

    const envInfo = exDigest.join('***');
    return basicInfo + envInfo;
}

/**
 * 数组是否有值
 * @param v 待检测值
 * @returns 是否是数组并且有值
 */
function hasArrayValue(v: any): boolean {
    return v && Array.isArray(v) && v.length > 0;
}

/**
 * 比较两帧，如果两帧文件名、函数、行、列均相同，则视为同一帧
 * @returns 两帧是否相同
 */
function compareFrame(f1: StackFrame, f2: StackFrame) {
    return (
        f1.filename === f2.filename &&
        f1.lineno === f2.lineno &&
        f1.colno === f2.colno &&
        f1['function'] === f2['function']
    );
}

/**
 * 将多个字符串或者正则表达式拼接成一个大的正则表达式
 * @param patterns 正则表达式或字符串数组，可混用
 * @returns 大的正则表达式
 */
export function joinRegExp(patterns: (string | RegExp)[]): RegExp {
    // Combine an array of regular expressions and strings into one large regexp
    // Be mad.
    const sources: any[] = [];
    const len = patterns.length;

    for (let i = 0; i < len; i++) {
        const pattern: any = patterns[i];
        if (isString(pattern)) {
            // If it's a string, we need to escape it
            // Taken from: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
            sources.push(pattern.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1'));
        } else if (pattern && pattern.source) {
            // If it's a regexp already, we want to extract the source
            sources.push(pattern.source);
        }
    }
    return new RegExp(sources.join('|'), 'i');
}

/**
 * 对输入数据对象中的某些 key 对应的值进行打码操作
 * @param input 输入数据对象
 * @param sanitizeKeys 过滤的 keys
 * @returns 打码后的对象或者数组
 */
export function sanitize(input: any, sanitizeKeys: (string | RegExp)[]) {
    if (!Array.isArray(sanitizeKeys) || (Array.isArray(sanitizeKeys) && sanitizeKeys.length === 0)) {
        return input;
    }

    const sanitizeRegExp = joinRegExp(sanitizeKeys);
    const sanitizeMask = '********';
    let safeInput: any;

    try {
        // 复制一个对象
        safeInput = JSON.parse(stringify(input));
    } catch (o_O) {
        return input;
    }

    function sanitizeWorker(workerInput: any): any {
        if (Array.isArray(workerInput)) {
            return workerInput.map(function (val) {
                return sanitizeWorker(val);
            });
        }

        if (isPlainObject(workerInput)) {
            return Object.keys(workerInput).reduce(function (acc, k) {
                if (sanitizeRegExp.test(k)) {
                    acc[k] = sanitizeMask;
                } else {
                    acc[k] = sanitizeWorker(workerInput[k]);
                }
                return acc;
            }, {});
        }

        return workerInput;
    }

    return sanitizeWorker(safeInput);
}

/**
 * 替换一些正则表达式的转移字符，便于在 new RegExp 中使用
 * @param str 原始字符串
 * @return 转义过的字符串
 */
export function regexStringTransform(str: string) {
    return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
}

/**
 * 检测 message 是否符合某个正则或者字符串数组
 * @param regexArray 正则或者字符串数组
 * @param message 待检测字符串
 * @returns 检测结果
 */
export function multipleTest(regexArray: Array<RegExp | string>, message: any): boolean {
    if (!isString(message)) {
        return false;
    }

    // 如果某个正则符合了就返回true
    return regexArray.some(r => {
        // 空校验
        if (!r) {
            return false;
        }
        if (isString(r)) {
            return message.indexOf(r) !== -1;
        }
        if (isRegExp(r)) {
            try {
                return (r as RegExp).test(message);
            } catch (e) {
                console.error(`${r}正则书写有误`);
                return false;
            }
        }
        console.error(`${r}忽略匹配项类型应该是String、RegExp`);
        return false;
    });
}

/** 计算 API 异常 ISSUE ID */
export function computeAPIExceptionIssueId(api: APIException) {
    return md5(api.method + '***' + formatURL(api.url));
}

/** 计算 API 异常事件 ID */
export function computeAPIExceptionDataId(apiData: any) {
    return md5(stringify(apiData));
}

export function transformResponse(responseType = '', response = '') {
    const strictJSONParsing = responseType === '' || responseType === 'text';

    if (strictJSONParsing && isString(response) && response.length) {
        try {
            return JSON.parse(response);
        } catch (e) {
            return response;
        }
    }

    if (responseType === 'json') {
        return JSON.parse(JSON.stringify(response));
    }

    // 对blob、ms-stream、arraybuffer、document 等暂不处理
    return '';
}

/**
 * 解析 URL 字段
 * - 模糊queryParams数字和中文
 * @param { string } URL字符串
 */
export function formatURL(rawURL) {
    if (!isString(rawURL)) {
        return '';
    }
    const urlArr = rawURL.split('?');
    if (urlArr[1]) {
        urlArr[1] = urlArr[1].replace(/\d+/g, '*').replace(/[\u4e00-\u9fa5]+/gi, '*');
        return urlArr.join('?');
    }
    return rawURL;
}

/**
 * 格式化截取用户行为数据
 * - 转成字符串截取
 * @param { string } 需要处理的数据
 */
export function formatBehaviorData(data) {
    const MAX_BEHAVIOR_LENGTH = 256;
    const result = isString(data) ? data : JSON.stringify(data);
    return result.slice(0, MAX_BEHAVIOR_LENGTH);
}
